import { getMessaging, getToken, isSupported, onMessage } from "firebase/messaging";
import { app } from "@/firebase/config";
import postItem from "@/composables/postItem";
import store from "@/store";
import {
  canMakeAuthenticatedRequest,
  isAuthExpired,
} from "@/composables/authRequestGuard";

const vapidKey = process.env.VUE_APP_FIREBASE_VAPID_KEY;
const tokenStorageKey = "easyhmo_fcm_token";
const tokenRegistrationStorageKey = "easyhmo_fcm_token_registration";

const isMessagingReady = async () => {
  return process.env.NODE_ENV === "production" &&
    Boolean(vapidKey) &&
    "Notification" in window &&
    await isSupported();
};

const deviceName = () => {
  const ua = navigator.userAgent || "";
  if (/Edg/i.test(ua)) return "Edge";
  if (/Chrome/i.test(ua)) return "Chrome";
  if (/Safari/i.test(ua)) return "Safari";
  if (/Firefox/i.test(ua)) return "Firefox";
  return "Browser";
};

const currentUserKey = () => {
  const user = store.state.user;
  return user?.id || user?.email || null;
};

const getStoredRegistration = () => {
  try {
    return JSON.parse(localStorage.getItem(tokenRegistrationStorageKey) || "null");
  } catch {
    return null;
  }
};

const clearStoredRegistration = () => {
  try {
    localStorage.removeItem(tokenStorageKey);
    localStorage.removeItem(tokenRegistrationStorageKey);
  } catch {
    // Storage can be unavailable in some browser modes.
  }
};

const isTokenRegistrationCurrent = (token) => {
  const stored = getStoredRegistration();

  return stored &&
    stored.token === token &&
    stored.user === currentUserKey() &&
    stored.device_name === deviceName();
};

const rememberTokenRegistration = (token) => {
  try {
    localStorage.setItem(tokenStorageKey, token);
    localStorage.setItem(tokenRegistrationStorageKey, JSON.stringify({
      token,
      user: currentUserKey(),
      device_name: deviceName(),
      registered_at: Date.now(),
    }));
  } catch {
    // Token sync should still succeed even if local storage is unavailable.
  }
};

const registerNotificationToken = async () => {
  if (isAuthExpired() || !canMakeAuthenticatedRequest("notification/tokens")) {
    return { status: "auth_expired" };
  }

  if (!store.state.user || !(await isMessagingReady())) {
    return { status: "unavailable" };
  }

  if (Notification.permission === "denied") {
    return { status: "denied" };
  }

  const permission = Notification.permission === "granted"
    ? "granted"
    : await Notification.requestPermission();

  if (permission !== "granted") {
    return { status: permission };
  }

  const registration = await navigator.serviceWorker.ready;
  const messaging = getMessaging(app);
  const token = await getToken(messaging, {
    vapidKey,
    serviceWorkerRegistration: registration,
  });

  if (!token) {
    return { status: "empty" };
  }

  if (isTokenRegistrationCurrent(token)) {
    return { status: "already_registered", token };
  }

  const { err, errorType } = await postItem("notification/tokens", {
    token,
    platform: "web",
    device_name: deviceName(),
  });

  if (err.value) {
    return {
      status: errorType.value === "auth" ? "auth_expired" : "failed",
      error: err.value,
    };
  }

  rememberTokenRegistration(token);

  return { status: "registered", token };
};

const syncNotificationToken = async () => {
  if (isAuthExpired() || !canMakeAuthenticatedRequest("notification/tokens")) {
    return { status: "auth_expired" };
  }

  if (!("Notification" in window)) {
    return { status: "unavailable" };
  }

  if (Notification.permission !== "granted") {
    return { status: Notification.permission };
  }

  return registerNotificationToken();
};

const revokeNotificationToken = async () => {
  const token = localStorage.getItem(tokenStorageKey);

  if (!token) {
    return;
  }

  if (!isAuthExpired() && canMakeAuthenticatedRequest("notification/tokens/revoke")) {
    await postItem("notification/tokens/revoke", { token });
  }

  clearStoredRegistration();
};

const listenForForegroundMessages = async (callback) => {
  if (isAuthExpired()) {
    return;
  }

  if (!(await isMessagingReady())) {
    return;
  }

  const messaging = getMessaging(app);
  onMessage(messaging, callback);
};

export {
  isMessagingReady,
  listenForForegroundMessages,
  registerNotificationToken,
  revokeNotificationToken,
  syncNotificationToken,
};
